import {
  normalizeWorkspacePath,
  sanitizeTabMemory,
  sanitizeWorkspaceSearchParams,
  type TabMemory,
  type TabSearchParams,
  type WorkspaceTab,
} from "./locations";

export interface OpenTabInStateResult {
  tabs: WorkspaceTab[];
  created: boolean;
}

export interface CloseTabInStateResult {
  outcome: "closed" | "not-found";
  tabs: WorkspaceTab[];
  nextActiveTab: WorkspaceTab | null;
}

export interface UpdateTabInStateResult {
  tabs: WorkspaceTab[];
  updated: boolean;
}

function normalizeWorkspaceTab(tab: WorkspaceTab): WorkspaceTab {
  const sanitizedSearch = sanitizeWorkspaceSearchParams(tab.kind, tab.searchParams);
  return {
    ...tab,
    currentPath: normalizeWorkspacePath(tab.currentPath),
    searchParams: sanitizedSearch.searchParams,
    memory: sanitizeTabMemory(tab.memory),
  };
}

export function normalizeTabPath(path: string): string {
  return normalizeWorkspacePath(path);
}

export function openTabInState(tabs: WorkspaceTab[], tab: WorkspaceTab): OpenTabInStateResult {
  const existing = tabs.some((item) => item.tabId === tab.tabId);
  if (existing) {
    return { tabs, created: false };
  }

  return {
    tabs: [...tabs, normalizeWorkspaceTab(tab)],
    created: true,
  };
}

export function closeTabInState(
  tabs: WorkspaceTab[],
  tabId: string,
  activeTabId: string | null
): CloseTabInStateResult {
  const closedIndex = tabs.findIndex((tab) => tab.tabId === tabId);
  if (closedIndex < 0) {
    return {
      outcome: "not-found",
      tabs,
      nextActiveTab: null,
    };
  }

  const nextTabs = tabs.filter((tab) => tab.tabId !== tabId);

  if (activeTabId !== tabId) {
    return {
      outcome: "closed",
      tabs: nextTabs,
      nextActiveTab: null,
    };
  }

  const nextIndex = Math.min(closedIndex, nextTabs.length - 1);
  const nextActiveTab = nextTabs[nextIndex] ?? null;

  return {
    outcome: "closed",
    tabs: nextTabs,
    nextActiveTab,
  };
}

export function updateTabPathInState(
  tabs: WorkspaceTab[],
  tabId: string,
  path: string
): UpdateTabInStateResult {
  const normalizedPath = normalizeWorkspacePath(path);
  let updated = false;

  const nextTabs = tabs.map((tab) => {
    if (tab.tabId !== tabId) {
      return tab;
    }

    if (tab.currentPath === normalizedPath) {
      return tab;
    }

    updated = true;
    return {
      ...tab,
      currentPath: normalizedPath,
    };
  });

  return {
    tabs: updated ? nextTabs : tabs,
    updated,
  };
}

export function mergeTabSearchParams(
  current: TabSearchParams,
  updates: TabSearchParams
): TabSearchParams {
  const merged: TabSearchParams = { ...current };
  let changed = false;

  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) {
      if (key in merged) {
        delete merged[key];
        changed = true;
      }
      continue;
    }

    if (merged[key] !== value) {
      merged[key] = value;
      changed = true;
    }
  }

  return changed ? merged : current;
}

function areTabSearchParamsEqual(a: TabSearchParams, b: TabSearchParams): boolean {
  const aEntries = Object.entries(a);
  const bEntries = Object.entries(b);

  if (aEntries.length !== bEntries.length) {
    return false;
  }

  return aEntries.every(([key, value]) => b[key] === value);
}

export function updateTabSearchParamsInState(
  tabs: WorkspaceTab[],
  tabId: string,
  params: TabSearchParams
): UpdateTabInStateResult {
  let updated = false;

  const nextTabs = tabs.map((tab) => {
    if (tab.tabId !== tabId) {
      return tab;
    }

    const currentSanitizedSearchParams = sanitizeWorkspaceSearchParams(
      tab.kind,
      tab.searchParams
    ).searchParams;
    const updatesSanitizedSearchParams = sanitizeWorkspaceSearchParams(tab.kind, params, {
      preserveUndefined: true,
    }).searchParams;
    const mergedSearchParams = mergeTabSearchParams(
      currentSanitizedSearchParams,
      updatesSanitizedSearchParams
    );
    if (mergedSearchParams === tab.searchParams) {
      return tab;
    }

    updated = true;
    return {
      ...tab,
      searchParams: mergedSearchParams,
    };
  });

  return {
    tabs: updated ? nextTabs : tabs,
    updated,
  };
}

export function replaceTabSearchParamsInState(
  tabs: WorkspaceTab[],
  tabId: string,
  searchParams: TabSearchParams
): UpdateTabInStateResult {
  let updated = false;

  const nextTabs = tabs.map((tab) => {
    if (tab.tabId !== tabId) {
      return tab;
    }

    const sanitizedSearchParams = sanitizeWorkspaceSearchParams(
      tab.kind,
      searchParams
    ).searchParams;
    if (areTabSearchParamsEqual(tab.searchParams, sanitizedSearchParams)) {
      return tab;
    }

    updated = true;
    return {
      ...tab,
      searchParams: sanitizedSearchParams,
    };
  });

  return {
    tabs: updated ? nextTabs : tabs,
    updated,
  };
}

function areTabMemoryEqual(a: TabMemory, b: TabMemory): boolean {
  return (
    a.entityPath === b.entityPath &&
    a.entityIconKey === b.entityIconKey &&
    a.assistantPath === b.assistantPath &&
    a.assistantDraftMarkdown === b.assistantDraftMarkdown
  );
}

export function updateTabMemoryInState(
  tabs: WorkspaceTab[],
  tabId: string,
  memory: TabMemory
): UpdateTabInStateResult {
  const sanitizedMemory = sanitizeTabMemory(memory);
  let updated = false;

  const nextTabs = tabs.map((tab) => {
    if (tab.tabId !== tabId) {
      return tab;
    }

    if (areTabMemoryEqual(tab.memory, sanitizedMemory)) {
      return tab;
    }

    updated = true;
    return {
      ...tab,
      memory: sanitizedMemory,
    };
  });

  return {
    tabs: updated ? nextTabs : tabs,
    updated,
  };
}
